import { ActivityType, CardTypesEnum, NotificationStatusColorType } from '@common-fe/common-fe';

import { StockQuoteType } from '@/common/constants';
import { whiteLabelingConfig } from '@/utils/whiteLabeling';

const checksStatusColors = {
  checkStatusPending: '#F9C134',
  checkStatusIssued: '#176AF6',
  checkStatusCleared: '#19B269',
  checkStatusVoided: '#E72626',
  checkStatusExpired: '#9BA3B3',
  checkStatusReissued: '#E72626',
  checkStatusExpiring: '#F9C134',
  checkStatusReissuing: '#F9C134',
  checkStatusVoiding: '#E72626',
  checkStatusReady: '#19B269',
  checkStatusRejected: '#E72626',
};

const feedbackErrorColors = {
  danger: whiteLabelingConfig?.colors?.danger || '#E72626',
  dangerBorder: whiteLabelingConfig?.colors?.dangerBorder || '#F8BCBC',
  dangerContainer: whiteLabelingConfig?.colors?.dangerContainer || '#FDE9E9',
  dangerHover: whiteLabelingConfig?.colors?.dangerHover || '#A41B1B',
  dangerActive: whiteLabelingConfig?.colors?.dangerActive || '#66120A',
};

const feedbackSuccessColors = {
  success: whiteLabelingConfig?.colors?.success || '#19B269',
  successBorder: whiteLabelingConfig?.colors?.successBorder || '#B8E7D1',
  successContainer: whiteLabelingConfig?.colors?.successContainer || '#E8F7F0',
};

const feedbackWarningColors = {
  warning: whiteLabelingConfig?.colors?.warning || '#F9C134',
  warningBorder: whiteLabelingConfig?.colors?.warningBorder || '#FDECC0',
  warningContainer: whiteLabelingConfig?.colors?.warningContainer || '#FEF9EB',
};

const feedbackInfoColors = {
  info: whiteLabelingConfig?.colors?.info || '#176AF6',
  infoBorder: whiteLabelingConfig?.colors?.infoBorder || '#B7D1FC',
  infoContainer: whiteLabelingConfig?.colors?.infoContainer || '#E8F0FE',
};

const backgroundColors = {
  accentActive: whiteLabelingConfig?.colors?.accentActive || '#176AF6',
  accentBorder: whiteLabelingConfig?.colors?.accentBorder || '#B7D1FC',
  accentContainer: whiteLabelingConfig?.colors?.accentContainer || '#E8F0FE',
  stressContainer: whiteLabelingConfig?.colors?.stressContainer || '#151F6D',
  background1: whiteLabelingConfig?.colors?.background1 || '#142641',
  background2: whiteLabelingConfig?.colors?.background2 || '#025248',
  background3: whiteLabelingConfig?.colors?.background3 || '#000000',

  border: whiteLabelingConfig?.colors?.border || '#B8BFCC',

  border1: whiteLabelingConfig?.colors?.border1 || '#E5E9F0',
  border2: whiteLabelingConfig?.colors?.border2 || '#EEF1F4',
  module: whiteLabelingConfig?.colors?.module || '#F7F9FA',
  canvas: whiteLabelingConfig?.colors?.canvas || '#FFFFFF',
  popupBg2: '#0F16210D',
  legend: whiteLabelingConfig?.colors?.legend || '#BCD1EF',
};

const useMyMoney = {
  missingDetail: whiteLabelingConfig?.colors?.missingDetail || '#FB6D2A',
  missingContainer: whiteLabelingConfig?.colors?.missingContainer || '#FFF3EE',
  missingBorder: whiteLabelingConfig?.colors?.missingBorder || '#FEDACA',
};

const investments = {
  fund1: whiteLabelingConfig?.colors?.investmentsFund1 || '#3F68FF',
  fund2: whiteLabelingConfig?.colors?.investmentsFund2 || '#10D8B9',
  fund3: whiteLabelingConfig?.colors?.investmentsFund3 || '#FB5C20',
  fund4: whiteLabelingConfig?.colors?.investmentsFund4 || '#FC990F',
  fund5: whiteLabelingConfig?.colors?.investmentsFund5 || '#FDD137',
  fund6: whiteLabelingConfig?.colors?.investmentsFund6 || '#7909AD',
  fund7: whiteLabelingConfig?.colors?.investmentsFund7 || '#E72626',
  fund8: whiteLabelingConfig?.colors?.investmentsFund8 || '#9BA3B3',
  fund9: whiteLabelingConfig?.colors?.investmentsFund9 || '#176AF6',
  fund10: whiteLabelingConfig?.colors?.investmentsFund10 || '#00B5EE',
  fund11: whiteLabelingConfig?.colors?.investmentsFund11 || '#CE8258',
  fund12: whiteLabelingConfig?.colors?.investmentsFund12 || '#BCD1EF',
  investmentsProfit: whiteLabelingConfig?.colors?.investmentsProfit || '#00FF93',
  investmentsLoss: whiteLabelingConfig?.colors?.investmentsLoss || '#EF235D',
  whatIsNew1: whiteLabelingConfig?.colors?.whatIsNew1 || '#025248',
  whatIsNew2: whiteLabelingConfig?.colors?.whatIsNew2 || '#6C9EE8',
  whatIsNew3: whiteLabelingConfig?.colors?.whatIsNew3 || '#A99880',
  investmentBackground: whiteLabelingConfig?.colors?.investmentBackground || '#142641',
  profitContainer: whiteLabelingConfig?.colors?.profitContainer || '#00FF9314',
  lossContainer: whiteLabelingConfig?.colors?.lossContainer || '#EF235D14',
  chart: whiteLabelingConfig?.colors?.chart || '#3F68FF',
  coinChart: whiteLabelingConfig?.colors?.coinChart || [
    '#B8E7D1',
    '#E8F7F0',
    '#C3F5DD',
    '#E8F7F0',
    '#E8F7F0',
  ],
};
const navigationColors = {
  navigationBackground: whiteLabelingConfig?.colors?.navigationBackground || '#142641',
  navigationBorder: whiteLabelingConfig?.colors?.navigationBorder || '#2D3C52',
  navigationAccent: whiteLabelingConfig?.colors?.navigationAccent || '#3F68FF',
  navigationAccent2: whiteLabelingConfig?.colors?.navigationAccent2 || '#00FF93',
  navigationTextDefault: whiteLabelingConfig?.colors?.navigationTextDefault || '#FFFFFF',
  navigationTextAccent: whiteLabelingConfig?.colors?.navigationTextAccent || '#FFFFFF',
  navigationIconDefault: whiteLabelingConfig?.colors?.navigationIconDefault || '#FFFFFF',
  navigationIconAccent: whiteLabelingConfig?.colors?.navigationIconAccent || '#FFFFFF',
  navigationAccentHover: whiteLabelingConfig?.colors?.navigationAccentHover || '#334766',
  navigationAccentActive: whiteLabelingConfig?.colors?.navigationAccentActive || '#213A7A',
};
const hierarchyEntitiesColors = {
  partner: '#D83B00',
  distributor: '#904D27',
  employer: '#174FBC',
  subsidiary: '#0F7970',
  subgroup: '#481A54',
  employee: '#0CAD09',
  dependent: '#5E1696',
  noAssignee: '#B8BFCC',
};

const hierarchyEntitiesShadowColors = {
  employeeShadow: '#C8EACA',
  dependentShadow: '#D9CBE6',
};

const claimColors = {
  nonClaimIcon: whiteLabelingConfig?.colors?.nonClaimIcon || '#151F6D',
  nonClaimBg: whiteLabelingConfig?.colors?.nonClaimBg || '#E5E9F0',
};

const buttonColors = {
  buttonPrimary: whiteLabelingConfig?.colors?.buttonPrimary || '#3F68FF',
  buttonPrimaryHover: whiteLabelingConfig?.colors?.buttonPrimaryHover || '#1052C8',
  buttonPrimaryActive: whiteLabelingConfig?.colors?.buttonPrimaryActive || '#093999',
  buttonPrimaryPressed: whiteLabelingConfig?.colors?.buttonPrimaryPressed || '#1052C8',
  buttonPrimaryDisabled: whiteLabelingConfig?.colors?.buttonPrimaryDisabled || '#E5E9F0',

  buttonSecondaryHoverBorder: whiteLabelingConfig?.colors?.buttonSecondaryHoverBorder || '#3F68FF',
  buttonSecondaryActiveBorder: whiteLabelingConfig?.colors?.buttonSecondaryActiveBorder || '#093499',
  buttonSecondaryDisabledBorder: whiteLabelingConfig?.colors?.buttonSecondaryDisabledBorder || '#E5E9F0',
  buttonSecondaryDisabled: whiteLabelingConfig?.colors?.buttonSecondaryDisabled || '#FFFFFF',
  buttonSecondaryHover: whiteLabelingConfig?.colors?.buttonSecondaryHover || '#E8F0F3',

  buttonTertiaryHover: whiteLabelingConfig?.colors?.buttonTertiaryHover || '#E8F0FE',
  buttonTertiaryPressed: whiteLabelingConfig?.colors?.buttonTertiaryPressed || '#B7D1FC',
  buttonTertiaryDisabled: whiteLabelingConfig?.colors?.buttonTertiaryDisabled || '#E5E9F0',

  buttonSecondaryContainer: whiteLabelingConfig?.colors?.buttonSecondaryContainer || 'transparent',

  // button: -> buttonPrimary
  // buttonHover -> buttonPrimaryHover
  // buttonActive -> buttonPrimaryActive
  // buttonSecondary -> buttonSecondaryHoverBorder
  // buttonSecondaryContainer -> transparent
  // buttonSecondaryActive -> buttonSecondaryActiveBorder
};

const baseColors = {
  popup: whiteLabelingConfig?.colors?.popup || 'rgba(15, 22, 33, 0.5)',
  popup2: whiteLabelingConfig?.colors?.popup2 || 'rgba(15, 22, 33, 0.05)',

  buttonText: whiteLabelingConfig?.colors?.buttonText || '#FFFFFF',
  textTitle: whiteLabelingConfig?.colors?.textTitle || '#0F1621',
  textBody: whiteLabelingConfig?.colors?.textBody || '#0F1621',
  textSecondary: whiteLabelingConfig?.colors?.textSecondary || '#6B7485',
  textDisabled: whiteLabelingConfig?.colors?.textDisabled || '#9BA3B3',
  textOnColor: whiteLabelingConfig?.colors?.textOnColor || '#FFFFFF',
  textAccent: whiteLabelingConfig?.colors?.textAccent || '#176AF6',
  textAccentHover: whiteLabelingConfig?.colors?.textAccentHover || '#1052C8',
  textAccentActive: whiteLabelingConfig?.colors?.textAccentActive || '#093999',
  textShade: whiteLabelingConfig?.colors?.textShade || '#A6A6A6',

  iconPrimary: whiteLabelingConfig?.colors?.iconPrimary || '#0F1621',
  iconSecondary: whiteLabelingConfig?.colors?.iconSecondary || '#9BA3B3',
  iconOnColor: whiteLabelingConfig?.colors?.iconOnColor || '#FFFFFF',
  iconAccent: whiteLabelingConfig?.colors?.iconAccent || '#176AF6',
  iconAccentHover: whiteLabelingConfig?.colors?.iconAccentHover || '#1052C8',
  iconAccentActive: whiteLabelingConfig?.colors?.iconAccentActive || '#093999',
  iconDisabled: whiteLabelingConfig?.colors?.iconDisabled || '#B8BFCC',

  fade1:
    whiteLabelingConfig?.colors?.fade1 || 'linear-gradient(360deg, #F7F9FA 0%, rgba(255, 255, 255, 0) 100%)',
  fade2: whiteLabelingConfig?.colors?.fade2 || 'linear-gradient(180deg, #F7F9FA 0%, #EEF1F4 100%)',
};

const darkBackgrounds = {
  onDarkModuleBackground: whiteLabelingConfig?.colors?.onDarkModuleBackground || '#22334C',
  onDarkBackground: whiteLabelingConfig?.colors?.onDarkBackground || '#142641',
};

const darkGradients = {
  onDarkGradient:
    whiteLabelingConfig?.colors?.onDarkGradient ||
    'linear-gradient(0deg, #22334C 0%, rgba(34, 51, 76, 0.95) 43.79%, rgba(34, 51, 76, 0.00) 100%);',
};
const darkIcons = {
  onDarkIconDisabled: whiteLabelingConfig?.colors?.onDarkIconDisabled || '#73767D',
  onDarkIconAccent:
    whiteLabelingConfig?.colors?.onDarkIconAccent || whiteLabelingConfig?.colors?.iconAccent || '#176AF6',
};
const darkTexts = {
  onDarkText: whiteLabelingConfig?.colors?.onDarkText || '#73767D',
};
const darkButtonColors = {
  onDarkButtonPrimary: whiteLabelingConfig?.colors?.onDarkButtonPrimary || '#3858AE',
  onDarkButtonPrimaryHover: whiteLabelingConfig?.colors?.onDarkButtonPrimaryHover || '#345993',
  onDarkButtonPrimaryActive: whiteLabelingConfig?.colors?.onDarkButtonPrimaryActive || '#38475D',
  onDarkButtonPrimaryPressed: whiteLabelingConfig?.colors?.onDarkButtonPrimaryPressed || '#2F3B4D',
  onDarkButtonPrimaryDisabled: whiteLabelingConfig?.colors?.onDarkButtonPrimaryDisabled || '#263F78',

  onDarkButtonSecondaryHoverBorder:
    whiteLabelingConfig?.colors?.onDarkButtonSecondaryHoverBorder || '#3858AE',
  onDarkButtonSecondaryActiveBorder:
    whiteLabelingConfig?.colors?.onDarkButtonSecondaryActiveBorder || '#6681AA',
  onDarkButtonSecondaryDisabledBorder:
    whiteLabelingConfig?.colors?.onDarkButtonSecondaryDisabledBorder || '#365178',
  onDarkButtonSecondaryDisabled: whiteLabelingConfig?.colors?.onDarkButtonSecondaryDisabled || '#152743',
  onDarkButtonSecondaryHover: whiteLabelingConfig?.colors?.onDarkButtonSecondaryHover || '#485873',
};

const statusColors = {
  lockedNull: whiteLabelingConfig?.colors?.lockedNull || '#000000',
  processingPending: whiteLabelingConfig?.colors?.processingPending || '#F9C134',
  needsHelp: whiteLabelingConfig?.colors?.needsHelp || '#FF9D0A',
  paidActiveComplete: whiteLabelingConfig?.colors?.paidActiveComplete || '#19B269',
  partiallyPaid: whiteLabelingConfig?.colors?.partiallyPaid || '#B9CC29',
  deniedErrorExpired: whiteLabelingConfig?.colors?.deniedErrorExpired || '#E72626',
  uploading: whiteLabelingConfig?.colors?.uploading || '#9BA3B3',
  waitingForFund: whiteLabelingConfig.colors?.waitingForFund || '#8EB2A1',
  uploadedApproved: whiteLabelingConfig?.colors?.uploadedApproved || '#176AF6',
  loa: whiteLabelingConfig?.colors?.loa || '#00B5EE',
  retirement: whiteLabelingConfig?.colors?.retirement || '#CE8258',
  future: whiteLabelingConfig?.colors?.future || '#7909AD',
};

const uploadLinkColors = {
  uploadLinkBackground: whiteLabelingConfig?.colors?.uploadLinkBackground || '#151F6D',
};

export default {
  ...darkIcons,
  ...darkTexts,
  ...statusColors,
  ...darkBackgrounds,
  ...darkGradients,
  ...darkButtonColors,
  ...whiteLabelingConfig?.colors,
  ...feedbackErrorColors,
  ...feedbackSuccessColors,
  ...feedbackWarningColors,
  ...feedbackInfoColors,
  ...backgroundColors,
  ...useMyMoney,
  ...claimColors,
  ...checksStatusColors,
  ...investments,
  ...hierarchyEntitiesColors,
  ...hierarchyEntitiesShadowColors,
  ...buttonColors,
  ...baseColors,
  ...navigationColors,
  ...uploadLinkColors,

  loginTitleColor: whiteLabelingConfig?.colors?.loginTitleColor || baseColors.textOnColor,

  navigationBorder: whiteLabelingConfig?.colors?.navigationBorder || navigationColors.navigationBorder,

  listHeaderBackgroundColor:
    whiteLabelingConfig?.colors?.listHeaderBackgroundColor || backgroundColors.legend,
  listHeaderItemColor: whiteLabelingConfig?.colors?.listHeaderItemColor || baseColors.textBody,

  activity: {
    [ActivityType.DENTAL]: whiteLabelingConfig?.colors?.transactionDental || '#176AF6',
    [ActivityType.VISION]: whiteLabelingConfig?.colors?.transactionVision || '#481A54',
    [ActivityType.MEDICAL]: whiteLabelingConfig?.colors?.transactionMedical || '#00C3B3',
    [ActivityType.OTHER]: whiteLabelingConfig?.colors?.transactionOther || '#FF9D0A',
    [ActivityType.PARKING]: whiteLabelingConfig?.colors?.transactionParking || '#323D9B',
    [ActivityType.TRANSIT]: whiteLabelingConfig?.colors?.transactionTransit || '#9BBF84',
    [ActivityType.ADOPTION]: whiteLabelingConfig?.colors?.transactionAdoption || '#0F7970',
    [ActivityType.DEPENDENT_CARE]: whiteLabelingConfig?.colors?.transactionDependentCare || '#CC3381',
    [ActivityType.PHARMACY]: whiteLabelingConfig?.colors?.transactionPharmacy || '#D83B00',
    [ActivityType.LIFE_STYLE]: whiteLabelingConfig?.colors?.transactionLifestyle || '#D3375C',
    [ActivityType.PREMIUM]: whiteLabelingConfig?.colors?.transactionPremium || '#3628D1',
    [ActivityType.TRAVEL]: whiteLabelingConfig?.colors?.transactionTravel || '#F26B31',
    [ActivityType.REFUND]: whiteLabelingConfig?.colors?.refund || '#588C0E',
    [ActivityType.DEFAULT]: whiteLabelingConfig?.colors?.transactionDefault || '#5E697E',
    [ActivityType.CLAIM_TRANSFER_INTERNAL]: whiteLabelingConfig?.colors?.claimTransferInternal || '#E5E9F0',
    [ActivityType.WELLNESS]: whiteLabelingConfig?.colors?.transactionWellness || '#9B31F2',
    [ActivityType.PLAN_SPECIFIC]: whiteLabelingConfig?.colors?.transactionPlanSpecific || '#00B5EE',

    [ActivityType.CONTRIBUTIONS]: claimColors.nonClaimBg,
    [ActivityType.FORFEITURE]: claimColors.nonClaimBg,
    [ActivityType.PAYROLL]: claimColors.nonClaimBg,
    [ActivityType.ADJUSTMENTS]: whiteLabelingConfig?.colors?.transactionAdjustment || '#588C0E',
    [ActivityType.ADJUSTMENT]: whiteLabelingConfig?.colors?.transactionAdjustment || '#588C0E',
    [ActivityType.CLAIM_BALANCE_ADJUSTMENT]: whiteLabelingConfig?.colors?.transactionAdjustment || '#588C0E',
    [ActivityType.CLAIM_BALANCE_ADJUSTMENT_EXTERNAL]:
      whiteLabelingConfig?.colors?.transactionAdjustment || '#588C0E',
    [ActivityType.CLAIM_BALANCE_ADJUSTMENT_EXTERNAL_REVERSE]:
      whiteLabelingConfig?.colors?.transactionAdjustment || '#588C0E',
    [ActivityType.ROLLOVER_BALANCE_ADJUSTMENT]:
      whiteLabelingConfig?.colors?.transactionAdjustment || '#588C0E',
    [ActivityType.ROLLOVER_BALANCE_ADJUSTMENT_CROSS_OMNIBUS]:
      whiteLabelingConfig?.colors?.transactionAdjustment || '#588C0E',
    [ActivityType.ROLLOVER_BALANCE_ADJUSTMENT_EXTERNAL]:
      whiteLabelingConfig?.colors?.transactionAdjustment || '#588C0E',
    [ActivityType.ROLLOVER_BALANCE_ADJUSTMENT_EXTERNAL_REVERSE]:
      whiteLabelingConfig?.colors?.transactionAdjustment || '#588C0E',
    [ActivityType.INTEREST]: claimColors.nonClaimBg,
    [ActivityType.TRANSFER]: claimColors.nonClaimBg,
    [ActivityType.INTERNAL_TRANSFER_OF_ASSETS]: claimColors.nonClaimBg,
    [ActivityType.OTHER_ROOT_INTERNAL_TRANSFER_OF_ASSETS]: claimColors.nonClaimBg,

    [ActivityType.TRANSFER_OF_ASSETS]: claimColors.nonClaimBg,
    [ActivityType.INBOUND_TRANSFER_OF_ASSETS]: claimColors.nonClaimBg,
    [ActivityType.OUTBOUND_TRANSFER_OF_ASSETS]: claimColors.nonClaimBg,
    [ActivityType.INVESTMENTS]: claimColors.nonClaimBg,
    [ActivityType.BUY_INVESTMENT]: claimColors.nonClaimBg,
    [ActivityType.SELL_INVESTMENT]: claimColors.nonClaimBg,
    [ActivityType.FEES]: claimColors.nonClaimBg,
    [ActivityType.PAYMENT_EE_FEE]: claimColors.nonClaimBg,
    [ActivityType.NOTIONAL_FUND]: claimColors.nonClaimBg,
    [ActivityType.NOTIONAL_PAYROLL]: claimColors.nonClaimBg,
    [ActivityType.ROLLOVER]: claimColors.nonClaimBg,
    [ActivityType.HSA_INCOMING_CONTRIBUTION]: claimColors.nonClaimBg,
    [ActivityType.HSA_ROLLOVER]: claimColors.nonClaimBg,
    [ActivityType.CLAIM_REPAYMENT]: claimColors.nonClaimBg,
    [ActivityType.HSA_INCOMING_CONTRIBUTION_REVERSE]: claimColors.nonClaimBg,
    [ActivityType.HSA_ROLLOVER_REVERSE]: claimColors.nonClaimBg,
    [ActivityType.PAYROLL_REVERSE]: claimColors.nonClaimBg,
    [ActivityType.ACCOUNT_MAINTENANCE_EE_FEE]: claimColors.nonClaimBg,
    [ActivityType.PAPER_STATEMENT_FEE]: claimColors.nonClaimBg,
    [ActivityType.FEE_TRANSFER_INTERNAL]: claimColors.nonClaimBg,
    [ActivityType.FEE_TRANSFER]: claimColors.nonClaimBg,
    [ActivityType.PAPER_STATEMENT_EE_FEE]: claimColors.nonClaimBg,
    [ActivityType.NOTIONAL_PAYROLL_REVERSE]: claimColors.nonClaimBg,
    [ActivityType.RECORD_KEEPING_PAYROLL]: claimColors.nonClaimBg,
  },
  cardTypes: {
    [CardTypesEnum.HSA]: whiteLabelingConfig?.colors?.planTypeHSA || '#2EA12B',
    [CardTypesEnum.DCAP]: whiteLabelingConfig?.colors?.planTypeDCAP || '#CC3381',
    [CardTypesEnum.HCFSA]: whiteLabelingConfig?.colors?.planTypeHCFSA || '#FA802A',
    [CardTypesEnum.HRA]: whiteLabelingConfig?.colors?.planTypeHRA || '#134E8F',
    [CardTypesEnum.PARKING]: whiteLabelingConfig?.colors?.planTypeCommuterParking || '#323D9B',
    [CardTypesEnum.TRANSIT]: whiteLabelingConfig?.colors?.planTypeCommuterTransit || '#9BBF84',
    [CardTypesEnum.ADOPTION]: whiteLabelingConfig?.colors?.planTypeAdoption || '#0F7970',
    [CardTypesEnum.SPECIALTY]: whiteLabelingConfig?.colors?.planTypeSpecialty || '#00B5EE',
    [CardTypesEnum.LIFESTYLE]: whiteLabelingConfig?.colors?.planTypeLifestyle || '#D3375C',
    [CardTypesEnum.TRAVEL]: whiteLabelingConfig?.colors?.planTypeTravel || '#F26B31',
    [CardTypesEnum.DENTAL]: whiteLabelingConfig?.colors?.planTypeDental || '#176AF6',
    [CardTypesEnum.RX]: whiteLabelingConfig?.colors?.planTypeRx || '#7909AD',
    [CardTypesEnum.MEDICAL]: whiteLabelingConfig?.colors?.planTypeMedical || '#00C3B3',
    [CardTypesEnum.VISION]: whiteLabelingConfig?.colors?.planTypeVision || '#481A54',
    [CardTypesEnum.EXTERNAL]: whiteLabelingConfig?.colors?.planTypeVision || '#481A54',
  },
  statusColors: statusColors,
  statusContainerColor: {
    lockedNullContainer: whiteLabelingConfig.colors?.lockedNullContainer || '#E6E6E6',
    processingPendingContainer: whiteLabelingConfig.colors?.processingPendingContainer || '#FEF9EB',
    needsHelpContainer: whiteLabelingConfig.colors?.needsHelpContainer || '#FFF5E6',
    paidActiveCompleteContainer: whiteLabelingConfig.colors?.paidActiveCompleteContainer || '#E8F7F0',
    partiallyPaidContainer: whiteLabelingConfig.colors?.partiallyPaidContainer || '#F8FAEA',
    deniedErrorExpiredContainer: whiteLabelingConfig.colors?.deniedErrorExpiredContainer || '#FDE9E9',
    uploadingContainer: whiteLabelingConfig.colors?.uploadingContainer || '#F5F6F7',
    waitingForFundContainer: whiteLabelingConfig.colors?.waitingForFundContainer || '#F4F7F6',
    uploadedApprovedContainer: whiteLabelingConfig.colors?.uploadedApprovedContainer || '#E8F0FE',
    loaContainer: whiteLabelingConfig.colors?.loaContainer || '#E6F8FD',
    retirementContainer: whiteLabelingConfig.colors?.retirementContainer || '#FAF3EE',
    futureContainer: whiteLabelingConfig.colors?.futureContainer || '#F2E6F7',
  },
  fileManagerTypes: {
    cencusType: whiteLabelingConfig.colors?.cencusType || '#323D9B',
    enrollmentType: whiteLabelingConfig.colors?.enrollmentType || '#C64B8A',
    contributionType: whiteLabelingConfig.colors?.contributionType || '#0F7970',
    carrierType: whiteLabelingConfig.colors?.carrierType || '#0C95D0',
    mixType: whiteLabelingConfig.colors?.mixType || '#481A54',
    unknownType: whiteLabelingConfig.colors?.unknownType || '#E72626',
  },

  // // File Manager Record Type

  notificationStatusIcon: {
    [NotificationStatusColorType.WARNING]: feedbackWarningColors.warning,
    [NotificationStatusColorType.SUCCESS]: feedbackSuccessColors.success,
    [NotificationStatusColorType.ERROR]: feedbackErrorColors.danger,
    [NotificationStatusColorType.WARNING_LIGHT]: feedbackWarningColors.warningContainer,
    [NotificationStatusColorType.SUCCESS_LIGHT]: feedbackSuccessColors.successContainer,
    [NotificationStatusColorType.ERROR_LIGHT]: feedbackErrorColors.dangerContainer,
    [NotificationStatusColorType.DEFAULT]: backgroundColors.border2,
  },
  stocksIcon: {
    [StockQuoteType.VTSMX]: investments.fund1,
    [StockQuoteType.VRIF]: investments.fund2,
    [StockQuoteType.SPDR]: investments.fund3,
    [StockQuoteType.SCHD]: investments.fund4,
    [StockQuoteType.TSLA]: investments.fund5,
  },
  stocksColors: [
    investments.fund1,
    investments.fund2,
    investments.fund3,
    investments.fund4,
    investments.fund5,
    investments.fund6,
    investments.fund7,
    investments.fund8,
    investments.fund9,
    investments.fund10,
    investments.fund11,
    investments.fund12,
  ],
};
